import { CompilationPlayerTrack, StreamingPlayerError } from '@heardis/stream-contracts';
import { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useConfigContext } from '../../contexts/ConfigContext';
import { MediaStatus, PlaybackStatus, PlayerContext, TrackListContext, usePlayerContext } from '../../contexts/PlayerContext';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { AnalyticsEventType } from '../../hooks/analytics/analytics.types';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import { fetchTracks, getCompilationPlayerTrackById, preloadPlayerTrack } from '../../utils/tracks';
import InfoContainer from '../InfoContainer/InfoContainer';
import { classNames } from '../../utils/styles';
import classes from './CompilationPlayer.module.css';
import LoadingCircle from '../../elements/icons/LoadingCircle';

interface CompilationState {
  list: CompilationPlayerTrack[];
  /** @TOASK was this "null" in the index really necessary? When could it be null? */
  index: number | null;
}

export interface CompilationPlayerProps {
  children: React.ReactNode;
  autoplay: boolean;
}

function CompilationPlayer({ children, autoplay }: CompilationPlayerProps): JSX.Element {
  const { api, private: authEnabled, ui } = useConfigContext();
  const { cache, setError } = useSettingsContext();
  const { user } = useAuthContext();
  const player = usePlayerContext();
  const { trackAnalyticsEvent } = useAnalytics();
  const [queue, setQueue] = useState<CompilationState>({ list: [], index: -1 });
  const initializedRef = useRef(false);
  const [refetchAttempts, setRefetchAttempts] = useState(0);
  const [unexpectedLoading, setUnexpectedLoading] = useState(false);

  async function fetchCompilationTracks(delay = 1000) {
    const currentReconnectDelay = delay;
    const maxReconnectDelay = 12000;
    const { tracks, status } = await fetchTracks<CompilationPlayerTrack>(api.endpoint, cache, authEnabled, user);

    if (status.error === StreamingPlayerError.MISSING_API_ENDPOINT && currentReconnectDelay < maxReconnectDelay) {
      const newDelay = currentReconnectDelay + Math.floor(Math.random() * 3000);
      setTimeout(async () => fetchCompilationTracks(newDelay), newDelay);
      setUnexpectedLoading(true);
      return;
    }
    if (status.error) {
      setUnexpectedLoading(false);
      setError({ code: status.error, message: 'Failed to fetch tracks.' });
      trackAnalyticsEvent({ type: AnalyticsEventType.PLAYER_ERROR, error: status.error });
    }
    setQueue(() => ({ list: tracks, index: 0 }));
    setUnexpectedLoading(false);
  }

  /** @TODO when playing the next track in the progress bar got one unnecessary jump */
  async function playTrack(forcePlay: boolean, position = 0) {
    if (queue.index === null) return;
    const track = queue.list[queue.index];
    if (!track) return;
    const compilationPlayerTrack = await getCompilationPlayerTrackById(api.endpoint, track.id);
    if (compilationPlayerTrack) {
      const preloadedPlayerTrack = preloadPlayerTrack(compilationPlayerTrack);
      player.load(preloadedPlayerTrack, position, forcePlay);
    } else {
      setError({ code: StreamingPlayerError.FAILED_TO_FETCH_TRACK, message: 'Failed to fetch track.' });
      trackAnalyticsEvent({ type: AnalyticsEventType.PLAYER_ERROR, error: StreamingPlayerError.FAILED_TO_FETCH_TRACK, track_id: track.id });
    }
  }

  function previous() {
    if (queue.index === null) return;
    const newIndex = queue.index === 0 ? queue.list.length - 1 : queue.index - 1;
    setQueue((prev) => ({ ...prev, index: newIndex }));
    trackAnalyticsEvent({
      type: AnalyticsEventType.GO_TO_PREVIOUS_TRACK,
      track_id_current: queue.list[queue.index].id,
      track_id_target: queue.list[newIndex].id,
    });
  }
  function next() {
    if (queue.index === null) return;
    const newIndex = queue.index === queue.list.length - 1 ? 0 : queue.index + 1;
    setQueue((prev) => ({ ...prev, index: newIndex }));
    trackAnalyticsEvent({
      type: AnalyticsEventType.GO_TO_NEXT_TRACK,
      track_id_current: queue.list[queue.index].id,
      track_id_target: queue.list[newIndex].id,
    });
  }
  function goTo(newIndex: number) {
    setQueue((prev) => ({ ...prev, index: newIndex }));

    if (queue.index === null) return;
    trackAnalyticsEvent({
      type: AnalyticsEventType.GO_TO_TRACK,
      track_id_current: queue.list[queue.index].id,
      track_id_target: queue.list[newIndex].id,
    });
  }

  function seek(progress: number) {
    player.seek(progress);

    /**
     * This AnalyticsEventType.TRACK_SEEK event could also been tracked on the player context level via the 'onseeked' listener
     * We implemented it here because the listener is also triggered when the radio station jumps to the current position
     */
    if (queue.index === null) return;
    trackAnalyticsEvent({
      type: AnalyticsEventType.TRACK_SEEK,
      track_id_current: queue.list[queue.index].id,
    });
  }

  const tracklistContext: TrackListContext = {
    tracks: queue.list,
    index: queue.index,
    goTo,
  };

  useEffect(() => {
    switch (player.mediaStatus) {
      case MediaStatus.ERROR:
        if (refetchAttempts >= 1) {
          player.pause();
          setError({ code: StreamingPlayerError.FAILED_TO_FETCH_TRACK, message: 'Failed to fetch track.' });
          return;
        }
        playTrack(player.playbackStatus === PlaybackStatus.PLAYING, player.currentTime);
        setRefetchAttempts((currentRefetchAttempts) => currentRefetchAttempts + 1);
        break;
      case MediaStatus.READY:
        setError(null);
        break;
      default:
        // do nothing
        break;
    }
  }, [player.mediaStatus]);

  useEffect(() => {
    fetchCompilationTracks();
  }, []);

  useEffect(() => {
    setRefetchAttempts(0);
    if (queue.index === null) return;

    if (!initializedRef.current && queue.index >= 0) {
      playTrack(autoplay);
      initializedRef.current = true;
    } else {
      playTrack(true);
    }
  }, [queue.index]);

  useEffect(() => {
    switch (player.playbackStatus) {
      case PlaybackStatus.STOPPED:
        next();
        break;
      default:
        // do nothing
        break;
    }
  }, [player.playbackStatus]);

  return (
    <PlayerContext.Provider value={{ ...player, next, previous, seek, tracklist: tracklistContext }}>
      {/* <MyDebugComponent>
        <li>{`Compilation player current index: ${queue.index}`}</li>
        <li>
          Compilation player trackList
          <ul>
            {queue.list.map((record, index) => (
              <li key={`${record.id}`}>{`${index}: ${record.artist} - ${record.title}`}</li>
            ))}
          </ul>
        </li>
      </MyDebugComponent> */}
      {children}
      <div className={classNames(classes.infoContainerWrapper, unexpectedLoading ? classes.active : '')}>
        <InfoContainer
          backgroundColor={ui.page.sections.secondary.background.color}
          typography={ui.page.sections.secondary.typography}
        >
          <h2 className={classes.infoContainerTitle}>
            <LoadingCircle />
            Loading the tracks
          </h2>
          <p>
            Loading the tracks is taking longer than expected. Please wait while we are trying to load the data.
          </p>
        </InfoContainer>
      </div>
    </PlayerContext.Provider>
  );
}

export default CompilationPlayer;
